import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Check, Loader2, Lock } from 'lucide-react';

const SecurityCheck = ({ onVerified, children }) => {
  const [stage, setStage] = useState('checking'); // checking, verifying, complete
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    // Simulate security check process
    const checkSequence = async () => {
      // Stage 1: Initial check (0-30%)
      setStage('checking');
      for (let i = 0; i <= 30; i += 2) {
        await new Promise(r => setTimeout(r, 30));
        setProgress(i);
      }
      
      // Stage 2: Verifying (30-80%)
      setStage('verifying');
      for (let i = 30; i <= 80; i += 3) {
        await new Promise(r => setTimeout(r, 40));
        setProgress(i);
      }
      
      // Stage 3: Complete (80-100%)
      for (let i = 80; i <= 100; i += 5) {
        await new Promise(r => setTimeout(r, 25));
        setProgress(i);
      }
      
      setStage('complete');
      
      // Small delay before showing the form
      await new Promise(r => setTimeout(r, 500));
      onVerified();
    };

    checkSequence();
  }, [onVerified]);

  const getStatusText = () => {
    switch (stage) {
      case 'checking':
        return 'Checking your browser...';
      case 'verifying':
        return 'Verifying you are human...';
      case 'complete':
        return 'Verification complete';
      default:
        return 'Processing...';
    }
  };

  const getStatusIcon = () => {
    switch (stage) {
      case 'complete':
        return <Check className="w-6 h-6 text-green-400" />;
      default:
        return <Loader2 className="w-6 h-6 text-cyan-400 animate-spin" />;
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-h-screen bg-[hsl(220,20%,4%)] flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-md"
      >
        <div className="bg-[hsl(220,20%,7%)] border border-white/10 rounded-2xl p-8 shadow-2xl">
          {/* Logo and Brand */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-3 mb-4">
              <img src="/logo.png" alt="TRADERSCAPITAL" className="h-12 w-auto" />
              <span className="font-heading font-bold text-xl text-white">
                TRADERS<span className="text-cyan-400">CAPITAL</span>
              </span>
            </div>
            <p className="text-gray-400 text-sm">Secure Connection</p>
          </div>

          {/* Security Check Box */}
          <div className="bg-white/5 border border-white/10 rounded-xl p-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                stage === 'complete' ? 'bg-green-500/20' : 'bg-cyan-500/20'
              }`}>
                <Shield className={`w-5 h-5 ${stage === 'complete' ? 'text-green-400' : 'text-cyan-400'}`} />
              </div>
              <div>
                <h3 className="font-medium text-white">Security Check</h3>
                <p className="text-xs text-gray-500">traderscapital.net</p>
              </div>
            </div>

            {/* Progress Section */}
            <div className="space-y-4">
              {/* Status Row */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {getStatusIcon()}
                  <span className="text-sm text-gray-300">{getStatusText()}</span>
                </div>
                {stage === 'complete' && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center"
                  >
                    <Check className="w-4 h-4 text-green-400" />
                  </motion.div>
                )}
              </div>

              {/* Progress Bar */}
              <div className="h-1.5 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className={`h-full rounded-full ${
                    stage === 'complete' 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-500' 
                      : 'bg-gradient-to-r from-blue-500 to-cyan-500'
                  }`}
                  initial={{ width: 0 }}
                  animate={{ width: `${progress}%` }}
                  transition={{ duration: 0.1 }}
                />
              </div>

              {/* Security Checks List */}
              <div className="space-y-2 pt-2">
                <SecurityCheckItem 
                  text="Browser verified" 
                  checked={progress >= 30} 
                  loading={stage === 'checking' && progress < 30}
                />
                <SecurityCheckItem 
                  text="Connection secure" 
                  checked={progress >= 60} 
                  loading={stage === 'verifying' && progress < 60}
                />
                <SecurityCheckItem 
                  text="Identity confirmed" 
                  checked={progress >= 100} 
                  loading={stage === 'verifying' && progress >= 60 && progress < 100}
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-center gap-2 mt-6 text-xs text-gray-500">
            <Lock className="w-3 h-3" />
            <span>Protected by TRADERSCAPITAL Security</span>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Security Check Item Component
const SecurityCheckItem = ({ text, checked, loading }) => {
  return (
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 flex items-center justify-center">
        {loading && <Loader2 className="w-3 h-3 text-cyan-400 animate-spin" />}
        {checked && !loading && (
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
          >
            <Check className="w-3 h-3 text-green-400" />
          </motion.div>
        )}
        {!loading && !checked && <div className="w-2 h-2 rounded-full bg-gray-600" />}
      </div>
      <span className={`text-xs ${checked ? 'text-gray-300' : 'text-gray-500'}`}>{text}</span>
    </div>
  );
};

export default SecurityCheck;
